import * as i0 from '@angular/core';
import { Injectable, Inject, Optional, InjectionToken, SkipSelf, inject, Component, ChangeDetectionStrategy, ViewEncapsulation, Input, NgModule } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import * as i1 from '@ngneat/transloco';
import { TRANSLOCO_TRANSPILER, TRANSLOCO_MISSING_HANDLER, TRANSLOCO_SCOPE, TRANSLOCO_LANG, getPipeValue } from '@ngneat/transloco';
import { BehaviorSubject, Subscription, map, first, shareReplay, of, distinctUntilChanged, combineLatest, switchMap, concat, EMPTY, skip } from 'rxjs';

function hasProperty(subject, key) {
    return typeof subject === 'object' && subject !== null && key in subject;
}

/**
 * Checks whether the specified value conforms to the `ExternalLink` interface.
 *
 * @param   value Value that is to be checked.
 * @returns       `true` if the specified value conforms to the `ExternalLink` interface, `false` if not.
 */
function isExternalLinkObject(value) {
    return (typeof value === 'object' &&
        value !== null &&
        hasProperty(value, 'url') && typeof value.url === 'string' &&
        (!hasProperty(value, 'target') || value.target === undefined || value.target === null || typeof value.target === 'string'));
}

/**
 * Base class for transpilers that can transpile contents of a block that is bounded by specific start and end tokens.
 */
class BlockTranspiler {
    blockStartToken;
    blockEndToken;
    /**
     * Creates a new `BlockTranspiler` for the specified boundary tokens.
     */
    constructor(
    /** String that defines the start token for the block. */
    blockStartToken, 
    /** String that defines the end token for the block. */
    blockEndToken) {
        this.blockStartToken = blockStartToken;
        this.blockEndToken = blockEndToken;
    }
    /** @inheritdoc */
    tokenize(translation, offset) {
        function recognize(token) {
            return (translation.substring(offset, offset + token.length) === token
                ? { nextOffset: offset + token.length, token: new BlockBoundary(token) }
                : undefined);
        }
        return recognize(this.blockStartToken) ?? recognize(this.blockEndToken);
    }
    /** @inheritdoc */
    transpile(start, context) {
        const nextToken = context.tokens[start];
        if (!this.isStartTag(nextToken)) {
            return undefined;
        }
        const { nextOffset, renderers } = context.transpileUntil(start + 1, (token) => this.isEndTag(token));
        return {
            nextOffset: Math.min(nextOffset + 1, context.tokens.length),
            renderer: this.createRenderer(renderers),
        };
    }
    /**
     * Checks whether the specified token represents the start boundary of a block that can be transpiled.
     *
     * @param   token Token which is to be checked.
     * @returns       `true` if the token is a start boundary of the block.
     */
    isStartTag(token) {
        return token instanceof BlockBoundary && token.token === this.blockStartToken;
    }
    /**
     * Checks whether the specified token represents the end boundary of a block that can be transpiled.
     *
     * @param   token Token which is to be checked.
     * @returns       `true` if the token is a end boundary of the block.
     */
    isEndTag(token) {
        return token instanceof BlockBoundary && token.token === this.blockEndToken;
    }
}
/**
 * Class used for representing the boundary tokens of a block.
 */
class BlockBoundary {
    token;
    constructor(token) {
        this.token = token;
    }
}

/**
 * Factory used for creating different types of `TranslationMarkupRenderer` instances.
 */
class TranslationMarkupRendererFactory {
    /** Document instance used by the renderers to create the actual DOM nodes. */
    document;
    /**
     * Creates a new `TranslationMarkupRendererFactory` instance.
     *
     * @param document Document instance used by the renderers to create the actual DOM nodes.
     */
    constructor(document) {
        this.document = document; // eslint-disable-line @typescript-eslint/no-unsafe-assignment
    }
    /**
     * Creates a markup renderer to render text. This can be either a static text or on that dynamically resolved based on the translation
     * parameters.
     *
     * @param   text Text that is to be rendered or a function that returns the text to be rendered based on the translation parameters.
     * @returns      A `TranslationMarkupRenderer` that renders the specified text.
     */
    createTextRenderer(text) {
        const document = this.document;
        return function renderText(translationParameters) {
            return document.createTextNode(typeof text === 'string' ? text : text(translationParameters));
        };
    }
    createElementRenderer(elementTag, childRenderers) {
        const document = this.document;
        return function renderElement(translationParameters) {
            const element = document.createElement(elementTag);
            for (const renderChild of childRenderers ?? []) {
                element.appendChild(renderChild(translationParameters));
            }
            return element;
        };
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: TranslationMarkupRendererFactory, deps: [{ token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: TranslationMarkupRendererFactory, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: TranslationMarkupRendererFactory, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; } });

/**
 * Transpiler that parses bold tags (`[b]...[/b]`) and creates a renderer that wraps the content in an HTML bold element (`<b>...</b>`).
 */
class BoldTextTranspiler extends BlockTranspiler {
    rendererFactory;
    /**
     * Creates a `BoldTextTranspiler` instance that uses the specified renderer factory.
     */
    constructor(
    /** Renderer factory which will be used for creating the HTML bold element renderer. */
    rendererFactory) {
        super('[b]', '[/b]');
        this.rendererFactory = rendererFactory;
    }
    /** @inheritdoc */
    createRenderer(childRenderers) {
        return this.rendererFactory.createElementRenderer('b', childRenderers);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: BoldTextTranspiler, deps: [{ token: TranslationMarkupRendererFactory }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: BoldTextTranspiler });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: BoldTextTranspiler, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: TranslationMarkupRendererFactory }]; } });

/**
 * Transpiler for named (special start and end token) link blocks. This can be used instead of the more generic `LinkTranspiler` to create
 * context specific link block transpilers.
 */
class ContextualLinkBlockTranspiler extends BlockTranspiler {
    resolveLinkSpecification;
    rendererFactory;
    linkRenderers;
    /**
     * Creates a new `NamedLinkBlockTranspiler` for the specified start and end token.
     */
    constructor(
    /** Token that defined the start of the link. */
    linkStartToken, 
    /** Token that defined the end of the link. */
    linkEndToken, 
    /** Object that specifies how to resolve the link. */
    resolveLinkSpecification, 
    /** Renderer factory used to create the renderer for the HTML anchor element that will be substituted in place of the token. */
    rendererFactory, 
    /** Set of link renderers that are used to apply the link model from the translation parameter to the anchor element. */
    linkRenderers) {
        super(linkStartToken, linkEndToken);
        this.resolveLinkSpecification = resolveLinkSpecification;
        this.rendererFactory = rendererFactory;
        this.linkRenderers = linkRenderers;
    }
    /** @inheritdoc */
    createRenderer(childRenderers) {
        const anchorRenderer = this.rendererFactory.createElementRenderer('a', childRenderers);
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        const findLinkRenderer = (link) => this.linkRenderers.find((linkRenderer) => linkRenderer.supports(link));
        const resolveLinkSpecification = this.resolveLinkSpecification;
        return function namedLinkRenderer(translationParameters) {
            const anchorElement = anchorRenderer(translationParameters);
            const link = ('static' in resolveLinkSpecification ? resolveLinkSpecification.static :
                'parameterKey' in resolveLinkSpecification ? translationParameters[resolveLinkSpecification.parameterKey] :
                    resolveLinkSpecification.resolve(translationParameters));
            const linkRenderer = findLinkRenderer(link);
            if (linkRenderer) {
                linkRenderer.render(link, anchorElement);
            }
            return anchorElement;
        };
    }
}

/**
 * Transpiler that substitutes a fixed token. A subclass is responsible for rendering the actual markup that will be used instead of the
 * token. This is done by providing an implementation for the abstract `createRenderer` function.
 */
class SubstitutionTranspiler {
    substitutionToken;
    /** Object that holds the value of lazily initialized property. */
    deferred = {};
    /**
     * Creates a new `SubstitutionTranspiler` for the specified substitution token.
     */
    constructor(
    /** String representing the token that will be substituted with the output of the resulting renderer. */
    substitutionToken) {
        this.substitutionToken = substitutionToken;
    }
    /** @inheritdoc */
    tokenize(translation, offset) {
        if (translation.startsWith(this.substitutionToken, offset)) {
            return {
                nextOffset: offset + this.substitutionToken.length,
                token: new SubstitutionToken(this.substitutionToken),
            };
        }
        return undefined;
    }
    /** @inheritdoc */
    transpile(offset, { tokens }) {
        const nextToken = tokens[offset];
        if (!(nextToken instanceof SubstitutionToken) || nextToken.token !== this.substitutionToken) {
            return undefined;
        }
        return {
            nextOffset: offset + 1,
            renderer: this.renderer,
        };
    }
    /** Cached value of the result of the `createRenderer` function. */
    get renderer() {
        return this.deferred.renderer ?? (this.deferred.renderer = this.createRenderer());
    }
}
/**
 * Class for representing tokens that are to be replaced with the output of a specific markup renderer.
 */
class SubstitutionToken {
    token;
    /**
     *  Creates a new `SubstitutionToken` instance for the specified token.
     */
    constructor(token) {
        this.token = token;
    }
}

/**
 * A substitution transpiler that replaces a specific token with a link. This transpiler is quite useful for defining tokens that are only
 * valid within a specific context (i.e. a limited set of translation entries).
 */
class ContextualLinkSubstitutionTranspiler extends SubstitutionTranspiler {
    options;
    rendererFactory;
    linkRenderers;
    /**
     * Creates a new `ContextualLinkSubstitutionTranspiler` for the specified token and which uses the provided options.
     */
    constructor(
    /** The token which will be substituted with a link. */
    token, 
    /** Options that define how to resolve the label text and link model. */
    options, 
    /** Renderer factory used to create the renderer for the HTML anchor element that will be substituted in place of the token. */
    rendererFactory, 
    /** Set of link renderers that are used to apply the link model from the translation parameter to the anchor element. */
    linkRenderers) {
        super(token);
        this.options = options;
        this.rendererFactory = rendererFactory;
        this.linkRenderers = linkRenderers;
    }
    /** @inheritdoc */
    createRenderer() {
        const labelRenderer = this.rendererFactory.createTextRenderer((() => {
            const { label } = this.options;
            if ('static' in label) {
                return label.static;
            }
            if ('resolve' in label) {
                return label.resolve; // eslint-disable-line @typescript-eslint/unbound-method
            }
            return (translationParams) => String(translationParams[label.parameterKey] || '');
        })());
        const anchorRenderer = this.rendererFactory.createElementRenderer('a', [labelRenderer]);
        const options = this.options;
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        const findLinkRenderer = (link) => this.linkRenderers.find((linkRenderer) => linkRenderer.supports(link));
        return function renderSubstitutionLink(translationParameters) {
            const anchorElement = anchorRenderer(translationParameters);
            const link = ('static' in options.link ? options.link.static :
                'parameterKey' in options.link ? translationParameters[options.link.parameterKey] :
                    options.link.resolve(translationParameters));
            const linkRenderer = findLinkRenderer(link);
            if (linkRenderer) {
                linkRenderer.render(link, anchorElement);
            }
            return anchorElement;
        };
    }
}

/**
 * Transpiler that parses italic tags (`[i]...[/i]`) and creates a renderer that wraps the content in an HTML italic element (`<i>...</i>`).
 */
class ItalicTextTranspiler extends BlockTranspiler {
    rendererFactory;
    /**
     * Creates an `ItalicTextTranspiler` instance that uses the specified renderer factory.
     */
    constructor(
    /** Renderer factory which will be used for creating the HTML italic element renderer. */
    rendererFactory) {
        super('[i]', '[/i]');
        this.rendererFactory = rendererFactory;
    }
    /** @inheritdoc */
    createRenderer(childRenderers) {
        return this.rendererFactory.createElementRenderer('i', childRenderers);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: ItalicTextTranspiler, deps: [{ token: TranslationMarkupRendererFactory }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: ItalicTextTranspiler });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: ItalicTextTranspiler, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: TranslationMarkupRendererFactory }]; } });

/**
 * Creates an `UnboundValueProvider` for the specified value.
 *
 * @param value The value to inject.
 */
function useValue(value) {
    return { useValue: value };
}
/**
 * Creates an `UnboundClassProvider` for the specified class.
 *
 * @param providerClass Class to instantiate when being injected for a specific token.
 */
function useClass(providerClass) {
    return { useClass: providerClass };
}
/**
 * Creates an `UnboundExistingProvider` for the specified `ProviderToken`.
 *
 * @param providerToken Existing token to return (equivalent to `injector.get(useExisting)`).
 */
function useExisting(providerToken) {
    return { useExisting: providerToken };
}
/**
 * Creates an `UnboundFactoryProvider` based on the return type of the specified factory function.
 *
 * The usage of the `useFactory` function is preferred over manually creating an `UnboundFactoryProvider` object literal, because this
 * function will enforce type safe constraints on the dependencies (parameters of the factory function).
 *
 * @param factoryFunction A function to invoke to create a value for this `token`. The function is invoked with resolved values of the
 *                        specified dependencies (`ProviderToken`s)
 * @param dependencies    List of `token`s to be resolved by the injector. These values are then used as arguments for the factory function.
 */
function useFactory(// eslint-disable-line @typescript-eslint/no-explicit-any
factoryFunction, ...dependencies) {
    return {
        useFactory: factoryFunction,
        deps: dependencies,
    };
}
/**
 * Binds the given provider definition to the specified token and returns a `Provider` entry that can be used in the providers list of
 * Angular module definitions.
 *
 * @param token           Token which for which the provider is to be defined.
 * @param unboundProvider Definition of a provider which should be bound to the specified token.
 * @param options         Optional extra binding options.
 */
// eslint-disable-next-line complexity
function bindProvider(token, unboundProvider, options = {}) {
    return (unboundProvider ? ((typeof unboundProvider === 'function') ?
        [
            unboundProvider,
            {
                provide: token,
                useExisting: unboundProvider,
                multi: options.multi,
            },
        ] :
        (typeof unboundProvider === 'object' && 'useValue' in unboundProvider) ?
            {
                provide: token,
                useValue: unboundProvider.useValue,
                multi: options.multi,
            } :
            (typeof unboundProvider === 'object' && 'useClass' in unboundProvider) ?
                {
                    provide: token,
                    useClass: unboundProvider.useClass,
                    multi: options.multi,
                } :
                (typeof unboundProvider === 'object' && 'useExisting' in unboundProvider) ?
                    {
                        provide: token,
                        useExisting: unboundProvider.useExisting,
                        multi: options.multi,
                    } :
                    (typeof unboundProvider === 'object' && 'useFactory' in unboundProvider) ?
                        {
                            provide: token,
                            useFactory: unboundProvider.useFactory,
                            deps: unboundProvider.deps,
                            multi: options.multi,
                        } :
                        {
                            provide: token,
                            useFactory: () => unboundProvider,
                            multi: options.multi,
                        }) :
        options.default ? ((typeof options.default === 'function') ?
            [
                options.default,
                {
                    provide: token,
                    useExisting: options.default,
                    multi: options.multi,
                },
            ] :
            (typeof options.default === 'object' && 'useValue' in options.default) ?
                {
                    provide: token,
                    useValue: options.default.useValue,
                    multi: options.multi,
                } :
                (typeof options.default === 'object' && 'useClass' in options.default) ?
                    {
                        provide: token,
                        useClass: options.default.useClass,
                        multi: options.multi,
                    } :
                    (typeof options.default === 'object' && 'useExisting' in options.default) ?
                        {
                            provide: token,
                            useExisting: options.default.useExisting,
                            multi: options.multi,
                        } :
                        (typeof options.default === 'object' && 'useFactory' in options.default) ?
                            {
                                provide: token,
                                useFactory: options.default.useFactory,
                                deps: options.default.deps,
                                multi: options.multi,
                            } :
                            {
                                provide: token,
                                useFactory: () => options.default,
                                multi: options.multi,
                            }) :
            []);
}

/**
 * Interface for objects that can render a link. Note that this is abstract class instead of an interface so the class itself can be used as
 * token for injection of the link renderers.
 */
class LinkRenderer {
}
/**
 * Creates a provider definition for the specified link renderer. The resulting `Provider` object can be used to make the renderer available
 * within a module or component injector scope.
 *
 * @param linkRenderer Specification of the link renderer for which the provider definition is to be created.
 */
function provideLinkRenderer(linkRenderer) {
    return bindProvider(LinkRenderer, linkRenderer, { multi: true });
}

/**
 * Wraps the specified value in an array unless the value already is an array.
 *
 * @param   value Value that is to be wrapped in an array.
 * @returns       If the specified value is an array the specified value is returned as is, otherwise it will be wrapped in array,
 *                containing just the value.
 */
function asArray(value) {
    return Array.isArray(value) ? value : [value];
}
/**
 * Flattens a recursive array (of arbitrary depth) into a one dimensional array. If the specified value is just a scalar value, then it will
 * be wrapped in an array containing only that value.
 *
 * @param   value Value which is to be converted to a flattened array.
 * @returns       A one dimensional array containing all elements from the recursive array.
 */
function asFlatArray(value) {
    return Array.isArray(value)
        ? value.reduce((partialResult, element) => partialResult.concat(asFlatArray(element)), [])
        : [value];
}

/**
 * A transpiler that can be used to parse links in a translation value and convert those to a renderer that generates an HTML anchor
 * element. Supports the following syntax: `[link:parameterKey]...[/link]`, where `parameterKey` should be substituted with the actual key
 * of the parameter that stores the target link. Contents between the opening and closing tags will be recursively transpiled.
 */
class LinkTranspiler {
    rendererFactory;
    /** Set of link renderers that are used to apply the link model from the translation parameter to the anchor element. */
    linkRenderers;
    /**
     * Creates a new `LinkTranspiler` that uses the specified renderer factory for creating the HTML anchor element (`<a>`) and which
     * delegates the rendering of link models to the given set of link renderers.
     */
    constructor(
    /** Renderer factory which will be used for creating the HTML anchor element renderer. */
    rendererFactory, 
    /** Set of link renderers that are used to apply the link model from the translation parameter to the anchor element. */
    linkRenderers) {
        this.rendererFactory = rendererFactory;
        this.linkRenderers = !linkRenderers ? [] : asArray(linkRenderers);
    }
    /** @inheritdoc */
    tokenize(translation, offset) {
        if (translation.startsWith(LINK_END_TOKEN, offset)) {
            return {
                nextOffset: offset + LINK_END_TOKEN.length,
                token: LINK_END,
            };
        }
        if (!translation.startsWith(LINK_START_TOKEN, offset)) {
            return undefined;
        }
        const end = translation.indexOf(']', offset + LINK_START_TOKEN.length);
        if (end < 0) {
            return undefined;
        }
        const parameterKey = translation.substring(offset + LINK_START_TOKEN.length, end);
        return {
            nextOffset: end + 1,
            token: new LinkStart(parameterKey),
        };
    }
    /** @inheritdoc */
    transpile(start, context) {
        const nextToken = context.tokens[start];
        if (!(nextToken instanceof LinkStart)) {
            return undefined;
        }
        const { nextOffset, renderers } = context.transpileUntil(start + 1, (token) => token === LINK_END);
        return {
            nextOffset: Math.min(nextOffset + 1, context.tokens.length),
            renderer: this.createRenderer(nextToken.parameterKey, renderers),
        };
    }
    /**
     * Creates a renderer that generates an HTML anchor tag and applies the link model stored in the specified translation parameter.
     *
     * @param   parameterKey   Key that identifies the translation parameter containing the link model.
     * @param   childRenderers Transpiled content of the link.
     * @returns                A markup renderer that creates an HTML representation of the link.
     */
    createRenderer(parameterKey, childRenderers) {
        const anchorRenderer = this.rendererFactory.createElementRenderer('a', childRenderers);
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        const findLinkRenderer = (link) => this.linkRenderers.find((linkRenderer) => linkRenderer.supports(link));
        function renderLink(translationParameters) {
            const anchorElement = anchorRenderer(translationParameters);
            const link = translationParameters[parameterKey];
            const linkRenderer = findLinkRenderer(link);
            if (linkRenderer) {
                linkRenderer.render(link, anchorElement);
            }
            return anchorElement;
        }
        return renderLink;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: LinkTranspiler, deps: [{ token: TranslationMarkupRendererFactory }, { token: LinkRenderer, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: LinkTranspiler });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: LinkTranspiler, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: TranslationMarkupRendererFactory }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [LinkRenderer]
                }, {
                    type: Optional
                }] }]; } });
/** Prefix of a link start token. */
const LINK_START_TOKEN = '[link:';
/** String that defines the end tag of a link. */
const LINK_END_TOKEN = '[/link]';
/** Token that represents the closing tag of a link. */
const LINK_END = new (class LinkEnd {
})(); // eslint-disable-line @typescript-eslint/no-extraneous-class
/**
 * Class used for modeling the link start tokens.
 */
class LinkStart {
    parameterKey;
    /**
     * Creates a new `LinkStart` that references the specified parameter.
     */
    constructor(
    /** Key of the parameter which stores the link model. */
    parameterKey) {
        this.parameterKey = parameterKey;
    }
}

/**
 * Interpolation expression matcher for the default syntax of Transloco interpolation expressions: `{{...}}`.
 *
 * @param   translationValue String where the interpolation expression might be found.
 * @param   offset           Position within the string which should be checked for the presence of an interpolation expression.
 * @returns                  The length of the interpolation expression in number of characters or `undefined` if none was found.
 */
function defaultTranslationInterpolationExpressionMatcher(translationValue, offset) {
    if (!translationValue.startsWith('{{', offset)) {
        return undefined;
    }
    const expressionEnd = translationValue.indexOf('}}', offset);
    return expressionEnd >= 2 ? expressionEnd + 2 - offset : undefined;
}
/**
 * Factory function for the `defaultTranslationInterpolationExpressionMatcher`. Used for the `TRANSLATION_INTERPOLATION_EXPRESSION_MATCHER`
 * injection token.
 */
function defaultTranslationInterpolationExpressionMatcherFactory() {
    return { matchExpression: defaultTranslationInterpolationExpressionMatcher };
}
/**
 * Injection token that is used for defining the provider for the `InterpolationExpressionMatcher`, which is needed by the
 * `StringInterpolationTranspiler`.
 */
const TRANSLATION_INTERPOLATION_EXPRESSION_MATCHER = new InjectionToken('TRANSLATION_INTERPOLATION_EXPRESSION_MATCHER', {
    providedIn: 'root',
    factory: defaultTranslationInterpolationExpressionMatcherFactory,
});
/**
 * Markup transpiler that supports interpolation expressions, and which creates a renderer that expands the expression to string using the
 * `TranslocoTranspiler`. Since the `TranslocoTranspiler` can be overridden with a custom implementation that uses a different syntax this
 * class uses a (configurable) `InterpolationExpressionMatcher`. This expression matcher can be overridden with a different implementation
 * that supports the syntax of a custom `TranslocoTranspiler`.
 */
class StringInterpolationTranspiler {
    rendererFactory;
    translocoTranspiler;
    expressionMatcher;
    /**
     * Creates a new `StringInterpolationTranspiler` that uses the specified renderer factory, Transloco transpiler and expression matcher.
     */
    constructor(
    /** Renderer factory which will be used for rendering the text nodes containing the result of interpolation expressions. */
    rendererFactory, 
    /** The Transloco transpiler that will be used for evaluating interpolation expressions. */
    translocoTranspiler, 
    /** Matcher that can find interpolation expressions in a translation value which are supported by the Transloco transpiler. */
    expressionMatcher) {
        this.rendererFactory = rendererFactory;
        this.translocoTranspiler = translocoTranspiler;
        this.expressionMatcher = expressionMatcher;
    }
    /** @inheritdoc */
    tokenize(translation, offset) {
        const expressionLength = this.expressionMatcher.matchExpression(translation, offset);
        if (!expressionLength) {
            return undefined;
        }
        return {
            nextOffset: offset + expressionLength,
            token: new StringInterpolationSegment(translation.substring(offset, offset + expressionLength)),
        };
    }
    /** @inheritdoc */
    transpile(offset, { tokens, translation }) {
        const nextToken = tokens[offset];
        if (!(nextToken instanceof StringInterpolationSegment)) {
            return undefined;
        }
        const { interpolationExpression } = nextToken;
        return {
            nextOffset: offset + 1,
            renderer: this.rendererFactory.createTextRenderer((translationParameters) => String(this.translocoTranspiler.transpile(interpolationExpression, translationParameters, translation, '???'))),
        };
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: StringInterpolationTranspiler, deps: [{ token: TranslationMarkupRendererFactory }, { token: TRANSLOCO_TRANSPILER }, { token: TRANSLATION_INTERPOLATION_EXPRESSION_MATCHER }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: StringInterpolationTranspiler, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: StringInterpolationTranspiler, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: TranslationMarkupRendererFactory }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [TRANSLOCO_TRANSPILER]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [TRANSLATION_INTERPOLATION_EXPRESSION_MATCHER]
                }] }]; } });
/**
 * Token class for interpolation expressions.
 */
class StringInterpolationSegment {
    interpolationExpression;
    /**
     *  Creates a new `StringInterpolationSegment` for the specified expression.
     */
    constructor(interpolationExpression) {
        this.interpolationExpression = interpolationExpression;
    }
}

/**
 * Injectable factory service that provides convenience functions for easily creating contextual link transpiler instances.
 */
class ContextualLinkTranspilerFactory {
    rendererFactory;
    /** Set of link renderers that are used to apply the link model from the translation parameter to the anchor element. */
    linkRenderers;
    /**
     * Creates a new `ContextualLinkTranspilerFactory` that uses the specified renderer factory and link renderers for the creation of
     * `ContextualLinkBlockTranspiler` and `ContextualLinkSubstitutionTranspiler` instances.
     */
    constructor(
    /** Renderer factory used to create the renderer for the HTML anchor element that will be substituted in place of the token. */
    rendererFactory, 
    /** Set of link renderers that are used to apply the link model from the translation parameter to the anchor element. */
    linkRenderers) {
        this.rendererFactory = rendererFactory;
        this.linkRenderers = !linkRenderers ? [] : asArray(linkRenderers);
    }
    // Unified implementation of the `createBlockTranspiler` function signatures above.
    createBlockTranspiler(parameterKeyOrStartToken, endToken, resolveLinkSpecification) {
        if (endToken === undefined || resolveLinkSpecification === undefined) {
            const parameterKey = parameterKeyOrStartToken;
            return this.createBlockTranspiler(`[${parameterKey}]`, `[/${parameterKey}]`, { parameterKey });
        }
        const startToken = parameterKeyOrStartToken;
        return new ContextualLinkBlockTranspiler(startToken, endToken, resolveLinkSpecification, this.rendererFactory, this.linkRenderers);
    }
    // Unified implementation of the `createSubstitutionTranspiler` function signatures above.
    createSubstitutionTranspiler(parameterKeyOrToken, options) {
        if (options === undefined) {
            return this.createSubstitutionTranspiler(`[${parameterKeyOrToken}]`, {
                /* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return */
                label: { resolve: (params) => params[parameterKeyOrToken].label },
                link: { resolve: (params) => params[parameterKeyOrToken].link },
                /* eslint-enable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return */
            });
        }
        return new ContextualLinkSubstitutionTranspiler(parameterKeyOrToken, options, this.rendererFactory, this.linkRenderers);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: ContextualLinkTranspilerFactory, deps: [{ token: TranslationMarkupRendererFactory }, { token: LinkRenderer, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: ContextualLinkTranspilerFactory, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: ContextualLinkTranspilerFactory, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: TranslationMarkupRendererFactory }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [LinkRenderer]
                }, {
                    type: Optional
                }] }]; } });

/**
 * Link renderer for string values, where the value is interpreted as the URL to an external resource. The renderer will set the browser
 * target for the link to `_blank` (a new window/tab).
 */
class StringLinkRenderer extends LinkRenderer {
    /** @inheritdoc */
    supports(link) {
        return typeof link === 'string';
    }
    /** @inheritdoc */
    render(link, targetElement) {
        targetElement.href = link;
        targetElement.target = '_blank';
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: StringLinkRenderer, deps: null, target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: StringLinkRenderer });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: StringLinkRenderer, decorators: [{
            type: Injectable
        }] });
/**
 * Link renderer that supports rendering of `ExternalLink` objects.
 */
class ExternalLinkObjectLinkRenderer extends LinkRenderer {
    /** @inheritdoc */
    supports(link) {
        return isExternalLinkObject(link);
    }
    /** @inheritdoc */
    render(link, targetElement) {
        targetElement.href = link.url;
        if (typeof link.target === 'string') {
            targetElement.target = link.target;
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: ExternalLinkObjectLinkRenderer, deps: null, target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: ExternalLinkObjectLinkRenderer });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: ExternalLinkObjectLinkRenderer, decorators: [{
            type: Injectable
        }] });

/** Token used to define the `TranslationMarkupTranspiler` providers that should be available for injection. */
const TRANSLATION_MARKUP_TRANSPILER = new InjectionToken('TRANSLATION_MARKUP_TRANSPILER');
/**
 * Creates a provider definition for the specified markup transpiler. The resulting `Provider` object can be used to make the transpiler
 * available within a module or component injector scope.
 *
 * @param transpiler Specification of the markup transpiler for which the provider definition is to be created.
 */
function provideTranslationMarkupTranspiler(transpiler) {
    return bindProvider(TRANSLATION_MARKUP_TRANSPILER, transpiler, { multi: true });
}

/**
 * Factory function for the default set of markup transpilers. Can be included in the providers array of an `@NgModule` or `@Component`
 * decorated class.
 *
 * @returns A `Provider` array for the default Transloco markup transpilers.
 */
function defaultTranslocoMarkupTranspilers() {
    return [
        provideTranslationMarkupTranspiler(BoldTextTranspiler),
        provideTranslationMarkupTranspiler(ItalicTextTranspiler),
        provideTranslationMarkupTranspiler(LinkTranspiler),
        provideLinkRenderer(StringLinkRenderer),
        provideLinkRenderer(ExternalLinkObjectLinkRenderer),
    ];
}

/**
 * Returns a provider that makes transpilers from a parent injector available in the child injector. This is needed if you define
 * additional translation markup transpilers that are scoped to a specific module or component. Doing so will override the transpilers from
 * the parent injector. Including the provider from the `inheritTranslationMarkupTranspilers` function will make those transpilers also
 * available.
 */
function inheritTranslationMarkupTranspilers() {
    return {
        provide: TRANSLATION_MARKUP_TRANSPILER,
        useFactory: (transpilers) => transpilers,
        deps: [[SkipSelf, TRANSLATION_MARKUP_TRANSPILER]],
        multi: true,
    };
}

/** Injection token for defining the provider to resolve the transpiler used for string interpolation expressions. */
const STRING_INTERPOLATION_TRANSPILER = new InjectionToken('STRING_INTERPOLATION_TRANSPILER', {
    providedIn: 'root',
    factory: () => inject(StringInterpolationTranspiler),
});

/**
 * Selects the first projected value that matches the specified predicate. In case no matching value can be found, `undefined` will be
 * returned.
 *
 * @param   iterable Iterable that produces the values that will be projected.
 * @param   project  Mapping function that is applied to values from the iterable.
 * @param   match    Predicate function that determines whether the projected value should be returned by this function.
 * @returns          First project value from the iterable that matches the specified predicate or `undefined` no such value was found.
 */
function selectFirstWhere(iterable, project, match) {
    for (const value of iterable) {
        const projectedValue = project(value);
        if (match(projectedValue)) {
            return projectedValue;
        }
    }
    return undefined;
}

/**
 * Context used during the transpilation process.
 */
class TranslationMarkupTranspilerContext {
    tokens;
    translation;
    transpilers;
    /**
     * Creates a new transpiler context for the specified token sequence and transpilers.
     */
    constructor(
    /** Sequence of tokens representing the translation value that should be transpiled. */
    tokens, 
    /** Translation dictionary containing the translation that is transpiled. */
    translation, 
    /** Transpilers which are to be used to parse and convert the token sequence. */
    transpilers) {
        this.tokens = tokens;
        this.translation = translation;
        this.transpilers = transpilers;
    }
    /**
     * Attempts to transpile the token sequence at the specified offset. This is done by finding the first transpiler from the set of
     * available transpilers that can successfully parse the token sequence.
     *
     * @param   offset Position within the token sequence where the transpilation should be performed.
     * @returns        Transpilation result of the first transpiler in the transpiler set that can parse the token sequence at the specified
     *                 offset. `undefined` is returned if none if the transpilers can transpile the token sequence.
     */
    transpile(offset) {
        return selectFirstWhere(this.transpilers, (transpiler) => transpiler.transpile(offset, this), (result) => result !== undefined && result.nextOffset !== offset);
    }
    /**
     * Starts a sequential transpilation process that continues transpiling the token sequence until the end has been reached or when the
     * given `stopTranspiling` function returns `true`. The result is an object that contains the offset where the transpilation ended and
     * an array of the markup renderer functions that were created by the transpilers.
     *
     * @param   startOffset     Position within the token sequence where the transpilation process should be started.
     * @param   stopTranspiling Function that tells whether the sequential transpilation should stop at the specified offet and token at
     *                          that offset in the token sequence. The function must return `true` if the transpilation should stop.
     * @returns                 An object that describes the result of the sequential transpilation process: an array of the created
     *                          translation markup renderers (`renderers` property) and the offset where the process stopped (`nextOffset`).
     */
    transpileUntil(startOffset, stopTranspiling) {
        let offset = startOffset;
        const renderers = [];
        while (offset < this.tokens.length && !stopTranspiling(this.tokens[offset], offset)) {
            const transpileResult = this.transpile(offset);
            if (transpileResult) {
                renderers.push(transpileResult.renderer);
                offset = transpileResult.nextOffset;
            }
            else {
                offset++;
            }
        }
        return {
            renderers,
            nextOffset: offset,
        };
    }
}

/**
 * Checks whether the specified value is not exactly equal to `undefined`.
 *
 * @param   value Value which is to be checked.
 * @returns       `true` if the specified value is not exactly equal to `undefined` or `true` if it is.
 */
function notUndefined(value) {
    return value !== undefined;
}

/**
 * Creates a translation markup rendering function for the specified translation value.
 *
 * @param   translationValue Translation value containing markup tokens that is to be converted to a rendering function.
 * @param   transpilers      Ordered set of transpiler that are available for the conversion.
 * @param   translation      Translation dictionary containing the translation value.
 * @returns                  A rendering function that can render the translation given translation parameter to a specified target element.
 */
function createTranslationMarkupRenderer(translationValue, transpilers, translation) {
    const tokens = tokenize(translationValue, transpilers);
    const renderers = transpile(tokens, transpilers, translation);
    return function renderTranslation(target, translationParams) {
        while (target.lastChild) {
            target.removeChild(target.lastChild);
        }
        for (const render of renderers) {
            target.appendChild(render(translationParams));
        }
    };
}
/**
 * Converts the given translation value into a sequence of tokens that can be parsed by the specified transpilers. Note that the order of
 * the transpilers is used to resolve conflicts when two or more transpilers can recognize a token at a specific location within the
 * translation value. Tokens are generated based on the first transpiler that is able to create token at a certain offset.
 *
 * @param   translationValue Translation value that is to be tokenized.
 * @param   transpilers      Ordered set transpilers used to convert the translation value into tokens.
 * @returns                  A sequence of tokens which can be parsed by the given transpilers and converted into markup renderers.
 */
function tokenize(translationValue, transpilers) {
    const tokens = [];
    let offset = 0;
    while (offset < translationValue.length) {
        // eslint-disable-next-line @typescript-eslint/no-loop-func
        const tokenizeResult = selectFirstWhere(transpilers, (transpiler) => transpiler.tokenize(translationValue, offset), notUndefined);
        if (tokenizeResult) {
            offset = tokenizeResult.nextOffset;
            tokens.push(tokenizeResult.token);
        }
        else {
            offset++;
        }
    }
    return tokens;
}
/**
 * Transpiles the given sequence of tokens using the provded transpilers into a `TranslationMarkupRenderer`.
 *
 * @param   tokens      Token sequence that is to be transpiled.
 * @param   transpilers Transpilers which are to be used to parse and convert the token sequence.
 * @param   translation Translation dictionary containing the translation value that is represented by the given tokens.
 * @returns             A `TranslationMarkupRenderer` that was constructed by the transpilers from the token sequence.
 */
function transpile(tokens, transpilers, translation) {
    const transpilerContext = new TranslationMarkupTranspilerContext(tokens, translation, transpilers);
    return transpilerContext.transpileUntil(0, () => false).renderers;
}

/**
 * Observes the specified property and returns a stream that emits all values which are assigned to the property. When subscribing to the
 * resulting stream it will always first emit the current value of the property, followed by all new values that are assigned to it.
 *
 * @param   target Object containing the property.
 * @param   key    Key of the property that is to be observed.
 * @returns        A stream of all values that are assigned to the specified property, starting with the current value of the property.
 */
function observeProperty(target, key) {
    const propertyDescriptor = getPropertyDescriptor(target, key);
    const originalGetAccessor = propertyDescriptor?.get; // eslint-disable-line @typescript-eslint/unbound-method
    // If the specified property is already observed return the value stream that was previously created for this property.
    if (originalGetAccessor?.__value$) {
        return originalGetAccessor.__value$;
    }
    const originalSetAccessor = propertyDescriptor?.set; // eslint-disable-line @typescript-eslint/unbound-method
    const subject = new BehaviorSubject(target[key]);
    const value$ = subject.asObservable();
    const newGetAccessor = originalGetAccessor
        ? () => originalGetAccessor.call(target)
        : () => subject.getValue();
    newGetAccessor.__value$ = value$;
    Object.defineProperty(target, key, {
        get: newGetAccessor,
        set(newValue) {
            if (originalSetAccessor !== undefined) {
                originalSetAccessor.call(target, newValue);
            }
            const nextValue = originalGetAccessor ? originalGetAccessor.call(target) : newValue;
            if (nextValue !== subject.getValue()) {
                subject.next(nextValue);
            }
        },
    });
    return value$;
}
/**
 * Finds the property descriptor for the property `key` in the specified target. In contrast to `Object.getOwnPropertyDescriptor` this
 * function will descent the prototype hierarchy of the specified target to find the property descriptor, making it suitable for properties
 * that are define through accessor functions on classes.
 *
 * @param target Object that containing the property.
 * @param key    Key of the property whose descriptor is to be retrieved.
 * @returns      The descriptor for the specified property or `undefined` if no such property exists on the target object.
 */
function getPropertyDescriptor(target, key) {
    if (target === null || target === undefined) {
        return undefined;
    }
    const descriptor = Object.getOwnPropertyDescriptor(target, key);
    return descriptor ?? getPropertyDescriptor(Object.getPrototypeOf(target), key);
}

/**
 * Transpiler used to capture literal translation texts. This transpiler will always be used as the last transpiler during the tokenization
 * and transpilation process, so it can catch any character that would otherwise not be transpiled.
 */
class StringLiteralTranspiler {
    rendererFactory;
    /**
     * Creates a new `StringLiteralTranspiler` that uses the specified renderer factory.
     */
    constructor(
    /** Renderer factory used for creating the renderer that renders the literal string text in a text node. */
    rendererFactory) {
        this.rendererFactory = rendererFactory;
    }
    /** @inheritdoc */
    tokenize(translation, offset) {
        return {
            nextOffset: offset + 1,
            token: translation.charAt(offset),
        };
    }
    /** @inheritdoc */
    transpile(start, { tokens }) {
        let end = start;
        while (end < tokens.length && typeof tokens[end] === 'string') {
            end++;
        }
        if (end === start) {
            return undefined;
        }
        const stringLiteral = tokens.slice(start, end).join('');
        return {
            nextOffset: end,
            renderer: this.rendererFactory.createTextRenderer(stringLiteral),
        };
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: StringLiteralTranspiler, deps: [{ token: TranslationMarkupRendererFactory }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: StringLiteralTranspiler, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: StringLiteralTranspiler, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: TranslationMarkupRendererFactory }]; } });

/* eslint-disable @angular-eslint/no-input-rename, @angular-eslint/component-selector, @angular-eslint/use-component-view-encapsulation */
/**
 * Component that supports rendering of translations including HTML markup.
 */
class TranslocoMarkupComponent {
    hostElement;
    translocoService;
    missingHandler;
    providedScope;
    providedLanguage;
    providedTranspilers;
    stringInterpolationTranspiler;
    stringLiteralTranspiler;
    /**
     * Key of the translation text that needs to be displayed. If no value is specified, then the value of the `content` property will be
     * used as the translation value. When that property is also unspecified, this component will render an empty element. */
    translationKey;
    /** Translated text to display. Note that this property will be ignored if a `translationKey` is specified. */
    content;
    /** Optional object that contains the values to be used for parameterized translation texts. */
    translationParameters;
    /** Language in which the text is the displayed. Overrides the provided language and language set using the translation service. */
    inlineLanguage;
    /** Optional translation scope. */
    inlineScope;
    /** Transpilers used to parse and render translation texts with markup. Merged with the provided transpilers. */
    inlineTranspilers;
    /** Whether the inline transpilers should be merged with the provided ones. If set to `false` only the inline transpilers are used. */
    mergeTranspilers;
    /** Root subscription to which all subscriptions are added that depend on the life cycle of the component instance. */
    subscriptions = new Subscription();
    /**
     * Creates a new `TranslocoMarkupComponent` instance.
     */
    constructor(
    /** Reference to the element which serves as the host for the component instance. */
    hostElement, 
    /** `TranslocoService` instance that is to be used for loading translations and obtaining the Transloco configuration. */
    translocoService, 
    /** Handler used for translation keys for which no translation value is defined in the active language. */
    missingHandler, 
    /** Translation scope that is provided via the module/component injectors. */
    providedScope, 
    /** Language that is provided via the module/component injectors. */
    providedLanguage, 
    /** Markup transpilers provided via the module/component injectors. */
    providedTranspilers, 
    /** Transpiler that is used for expanding string interpolation expressions. */
    stringInterpolationTranspiler, 
    /** Transpiler used for rendering all unprocessed tokens in the translation values as literal text. */
    stringLiteralTranspiler) {
        this.hostElement = hostElement;
        this.translocoService = translocoService;
        this.missingHandler = missingHandler;
        this.providedScope = providedScope;
        this.providedLanguage = providedLanguage;
        this.providedTranspilers = providedTranspilers;
        this.stringInterpolationTranspiler = stringInterpolationTranspiler;
        this.stringLiteralTranspiler = stringLiteralTranspiler;
    }
    /**
     * Initializes the component. When this method is called it starts listening for input property changes on the component and language
     * changes from the `TranslocoService`. This results in a stream containing the rendering function, which is executed for every change
     * to render the translation text including markup to the host element of the component instance.
     */
    ngOnInit() {
        const translationKey$ = observeProperty(this, 'translationKey');
        const content$ = observeProperty(this, 'content');
        const translationParameters$ = observeProperty(this, 'translationParameters');
        const inlineLanguage$ = observeProperty(this, 'inlineLanguage');
        const inlineScope$ = observeProperty(this, 'inlineScope');
        const inlineTranspilers$ = observeProperty(this, 'inlineTranspilers');
        const mergeTranspilers$ = observeProperty(this, 'mergeTranspilers').pipe(map((mergeTranspilers) => mergeTranspilers !== false));
        // Create the language$ stream that defines which in what language the translation text should be displayed.
        const langChanges$ = this.translocoService.langChanges$;
        const activeLanguage$ = this.translocoService.config.reRenderOnLangChange ? langChanges$ : langChanges$.pipe(first(), shareReplay({ bufferSize: 1, refCount: true }));
        const language$ = resolveLanguage(inlineLanguage$, of(this.providedLanguage ?? undefined), activeLanguage$);
        // Create the scope$ stream that emits an array of scopes to be used for resolving the translation text.
        const scopes$ = inlineScope$.pipe(map((inlineScope) => inlineScope ? inlineScope : this.providedScope), distinctUntilChanged(), map((scope) => Array.isArray(scope) ? scope : [scope ? scope : undefined]));
        // Using the language$ and scope$ stream obtain the translation$ stream that emits the translation dictionary to use.
        const translation$ = combineLatest([language$, scopes$]).pipe(switchMap(([language, scopes]) => combineLatest(scopes.map((scope) => {
            const scopeName = getScopeName(scope);
            const path = getLanguagePath(language, scopeName);
            const inlineLoader = getScopeInlineLoader(scope);
            return this.translocoService._loadDependencies(path, inlineLoader);
        })).pipe(map(() => this.translocoService.getTranslation(language)))));
        // Define the translationValue$ stream that emits the (unparsed) translation text that is to be displayed.
        const translationValue$ = combineLatest([translationKey$, content$, translation$]).pipe(map(([key, content, translation]) => {
            if (key === undefined) {
                return { value: content ?? '', translation };
            }
            const useFallbackTranslation = this.translocoService.config.missingHandler.useFallbackTranslation;
            const firstFallbackLanguage = asArray(this.translocoService.config.fallbackLang ?? [])[0];
            const fallbackTranslation = useFallbackTranslation && firstFallbackLanguage ? [this.translocoService.getTranslation(firstFallbackLanguage)] : [];
            return this.getTranslationValue(key, [translation, ...fallbackTranslation]);
        }));
        // Create the transpilers$ stream based on the inline providers, the provided providers and the merge transpilers setting.
        const transpilers$ = combineLatest([inlineTranspilers$, mergeTranspilers$]).pipe(map(([inlineTranspilers, mergeTranspilers]) => [
            ...(inlineTranspilers ? asArray(inlineTranspilers) : []),
            ...(this.providedTranspilers && (!inlineTranspilers || mergeTranspilers) ? asFlatArray(this.providedTranspilers) : []),
            this.stringInterpolationTranspiler,
            this.stringLiteralTranspiler,
        ]));
        // Finally we can create the render$ stream that emits the rendering function based on the translation text and the tranpilers.
        const render$ = combineLatest([translationValue$, transpilers$]).pipe(map(([{ translation, value }, transpilers]) => createTranslationMarkupRenderer(value, transpilers, translation)));
        // By combining the render$ and translationParameters$ stream the component can render the translation whenever something changes.
        this.subscriptions.add(combineLatest([render$, translationParameters$]).subscribe(([render, translationParameters]) => render(this.hostElement.nativeElement, translationParameters ?? {})));
    }
    /**
     * Called when the component is destroyed. Will cleanup any active subscriptions.
     */
    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }
    /**
     * Determines the translation value for the specified `key` and `translations`. Will return the first value of a translation which
     * contains the specified key. If no such translation exists, the `TranslocoMissingHandler` is used to obtain a value. For translations
     * where the value is an empty string the `TranslocoMissingHandler` is also used to determine if such values are allowed. If so, the
     * function will also return an empty string as translation value. When empty values are not allowed these will be considered as a
     * missing translation value for the tested translation.
     *
     * @param   key          Key for which the translation value is to be retrieved.
     * @param   translations Set of translations which are to be checked for the translation value identified by `key` parameter.
     * @returns              An object that defines the translation value and the translation dictionary containing the value.
     */
    getTranslationValue(key, translations) {
        const allowEmptyValues = this.translocoService.config.missingHandler.allowEmpty;
        // Find the first translation containing the specified translation key and obtain the translation value.
        const result = selectFirstWhere(translations, (translation) => {
            const value = translation[key];
            if (value === undefined || value === '' && !allowEmptyValues) {
                return undefined;
            }
            return { value: String(value), translation };
        }, notUndefined);
        // Return the result or invoke the missing handler if no translation value was found in the specified translations.
        return result ?? {
            value: String(this.missingHandler.handle(key, {
                activeLang: this.translocoService.getActiveLang(),
                ...this.translocoService.config,
            })),
            translation: translations[0] ?? {},
        };
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: TranslocoMarkupComponent, deps: [{ token: i0.ElementRef }, { token: i1.TranslocoService }, { token: TRANSLOCO_MISSING_HANDLER }, { token: TRANSLOCO_SCOPE, optional: true }, { token: TRANSLOCO_LANG, optional: true }, { token: TRANSLATION_MARKUP_TRANSPILER, optional: true }, { token: STRING_INTERPOLATION_TRANSPILER }, { token: StringLiteralTranspiler }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.1.8", type: TranslocoMarkupComponent, isStandalone: true, selector: "transloco", inputs: { translationKey: ["key", "translationKey"], content: "content", translationParameters: ["params", "translationParameters"], inlineLanguage: ["lang", "inlineLanguage"], inlineScope: ["scope", "inlineScope"], inlineTranspilers: ["transpilers", "inlineTranspilers"], mergeTranspilers: "mergeTranspilers" }, ngImport: i0, template: '', isInline: true, styles: [":host{display:inline}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: TranslocoMarkupComponent, decorators: [{
            type: Component,
            args: [{ selector: 'transloco', template: '', changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, standalone: true, styles: [":host{display:inline}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i1.TranslocoService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [TRANSLOCO_MISSING_HANDLER]
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [TRANSLOCO_SCOPE]
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [TRANSLOCO_LANG]
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [TRANSLATION_MARKUP_TRANSPILER]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [STRING_INTERPOLATION_TRANSPILER]
                }] }, { type: StringLiteralTranspiler }]; }, propDecorators: { translationKey: [{
                type: Input,
                args: ['key']
            }], content: [{
                type: Input
            }], translationParameters: [{
                type: Input,
                args: ['params']
            }], inlineLanguage: [{
                type: Input,
                args: ['lang']
            }], inlineScope: [{
                type: Input,
                args: ['scope']
            }], inlineTranspilers: [{
                type: Input,
                args: ['transpilers']
            }], mergeTranspilers: [{
                type: Input
            }] } });
/**
 * Determines which language should be active given a sequence of streaming language specifiers. The active languages is resolved based on
 * the order of the language specifiers where the first non `undefined` language is emitted. If that language specifier does not specify a
 * static language (e.g. `'en|static'`), any changes from the downstream language specifiers will be allowed to 'override' the active
 * language.
 *
 * @param   languageSpecifiers An array of language specifier streams.
 * @returns                    An observable that emits the active language based in the language specifiers.
 */
function resolveLanguage(...languageSpecifiers) {
    return languageSpecifiers.reduceRight((downstreamLanguage$, languageSpecifier$) => languageSpecifier$.pipe(switchMap((languageSpecifier) => {
        if (languageSpecifier === undefined) {
            return downstreamLanguage$;
        }
        const [isStatic, language] = getPipeValue(languageSpecifier, 'static');
        return concat(of(language), isStatic ? EMPTY : downstreamLanguage$.pipe(skip(1)));
    })), of('')).pipe(distinctUntilChanged());
}
/**
 * Retrieves the name of the specified Transloco scope.
 *
 * @param   scope Scope for which the name is to be returned.
 * @returns       Name of the specified scope or `undefined` if the scope itself is `undefined`.
 */
function getScopeName(scope) {
    return typeof scope === 'object' ? scope.scope : scope;
}
/**
 * Determines the language path for the specified language and (optional) scope.
 *
 * @param  language Language identifier.
 * @param  scope    Scope name (optional).
 * @return          Language path.
 */
function getLanguagePath(language, scope) {
    return scope ? `${scope}/${language}` : language;
}
/**
 * Determines the `InlineLoader` for the specified scope (if it is defined).
 *
 * @param   scope Scope for which the `InlineLoader` is to be determined.
 * @returns       Inline loader for the specified scope or `undefined` if the scope does not define one.
 */
function getScopeInlineLoader(scope) {
    return typeof scope === 'object' ? scope.loader : undefined;
}

/**
 * Module which exports the `<transloco>` markup component.
 */
class TranslocoMarkupModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: TranslocoMarkupModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.1.8", ngImport: i0, type: TranslocoMarkupModule, imports: [TranslocoMarkupComponent], exports: [TranslocoMarkupComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: TranslocoMarkupModule });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.8", ngImport: i0, type: TranslocoMarkupModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [TranslocoMarkupComponent],
                    exports: [TranslocoMarkupComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { BlockTranspiler, BoldTextTranspiler, ContextualLinkBlockTranspiler, ContextualLinkSubstitutionTranspiler, ContextualLinkTranspilerFactory, ItalicTextTranspiler, LinkRenderer, LinkTranspiler, STRING_INTERPOLATION_TRANSPILER, SubstitutionTranspiler, TRANSLATION_INTERPOLATION_EXPRESSION_MATCHER, TRANSLATION_MARKUP_TRANSPILER, TranslationMarkupRendererFactory, TranslationMarkupTranspilerContext, TranslocoMarkupComponent, TranslocoMarkupModule, defaultTranslocoMarkupTranspilers, inheritTranslationMarkupTranspilers, isExternalLinkObject, provideLinkRenderer, provideTranslationMarkupTranspiler };
